// @ts-nocheck
/**
 * This module is responsible for managing the consent state of known trackers.
 * It provides functions to update the consent state of all known trackers that
 * are allowed for the user. It also provides functions to filter elements based
 * on a regex pattern from a known tracker and to delete cookies that match a
 * regex pattern.
 *
 * @module trackingManager
 */
import { getCookies, deleteCookie } from '@/utilities';
import store from '@/store';
import { coverBlockedIframesAndImages } from './detectBlockedElements';

/**
 * Retrieve all script, iframe, img, and link elements that have already been
 * updated by us. Elements that have been marked as ignored will not be
 * returned.
 *
 * @returns {Element[]}
 */
function getElements() {
  return Array.from(
    document.querySelectorAll('script, iframe, img, link'),
  ).filter((el) => {
    const { attributes } = el;
    if (!attributes) {
      return false;
    }
    /* Don't return elements that have been marked as ignored. */
    if (attributes.getNamedItem('data-concord-ignore')) {
      return false;
    }
    /* Return true or false based on whether or not 
      the element has data-concord-modified attribute with a value. */
    return !!attributes.getNamedItem('data-concord-modified')?.value;
  });
}

/**
 * Filter an elements array based on a regex pattern from a known tracker. The
 * data-concord-src or data-concord-inline value will be checked against the
 * regex, in that order. Will only return elements that have been modified based
 * on a consent state change.
 *
 * @param {Element[]} elements
 * @param {RegExp} regex
 * @returns {Element[]}
 */
function filterElements(elements, regex, modified) {
  return elements.filter((el) => {
    const src =
      el.getAttribute('data-concord-src') ||
      el.getAttribute('data-concord-href') ||
      el.getAttribute('data-concord-inline');
    if (
      src &&
      regex.test(src) &&
      el.getAttribute('data-concord-modified') !== modified
    ) {
      return true;
    }
    return false;
  });
}

/**
 * Deletes all cookies that match a regex pattern.
 *
 * @param {RegExp} regex
 */
function blockCookie({ regex }) {
  const cookies = getCookies();

  const blockCookies = cookies.filter(({ name }) => name.match(regex));
  blockCookies.forEach((c) => {
    deleteCookie({ name: c.name, domain: c.domain, path: c.path });
  });
}

/**
 * Updates the consent state of all filtered elements that match the regex
 * pattern of a known tracker that is allowed for the user. Elements are
 * filtered if they match an allowed tracker and were previously marked as
 * blocked. If the element has a src attribute, the src attribute will be
 * replaced with a data-concord-src attribute. If the element does not have a
 * src attribute, the src attribute will be removed. If the element is a script
 * and has a type attribute, the type attribute will be replaced with a
 * data-concord-type attribute. If the element is a script and does not have a
 * type attribute, the type attribute will be removed. If the element is an
 * iframe and has a display attribute, the display attribute will be replaced
 * with a data-concord-display attribute. If the element is an iframe and does
 * not have a display attribute, the display attribute will be removed.
 *
 * @param {Element[]} elements
 * @param {Object} tracker
 * @param {RegExp} tracker.regex
 */
function updateNodeConsents(elements, { regex, modified }) {
  /*
   * Get all elements that have a modified change that have a src attribute
   * that matches the regex pattern of a known tracker.
   */
  const matchingElements = filterElements(elements, regex, modified);

  if (matchingElements.length === 0) {
    return;
  }

  matchingElements.forEach((el) => {
    if (!el.parentNode) {
      return;
    }

    /* Get the data-concord-inline attribute if it exists for inline node processing. */
    const inlineSrc = el.getAttribute('data-concord-inline');

    /* Set the data-concord-modified attribute to the new modified value. */
    el.setAttribute('data-concord-modified', modified);

    /* If the element is an inline node, handle the node updates. Should only need to
    handle allowed to blocked changes here since inline nodes are removed from the DOM when
    blocked so only allowed inline nodes need to be processed here. */
    if (inlineSrc) {
      if (modified === 'blocked') {
        /* Clone the node and it's parent and store them for later,
        then remove the node from the DOM. */
        const clone = {
          node: el.cloneNode(true),
          parent: el.parentNode,
        };

        const updatedNodes = [...store.removedNodes, clone];
        store.updateRemovedNodes(updatedNodes);
        el.parentNode.removeChild(el);
      }
      /* Start processing non-inline nodes.
      If the node is being changed from blocked to allowed, handle the node updates. */
    } else if (modified === 'allowed') {
      /* Set the src or href attribute. All elements here should have a data-concord-src or data-concord-href attribute. */
      if (el.hasAttribute('data-concord-src')) {
        el.setAttribute('src', el.getAttribute('data-concord-src'));
      } else if (el.hasAttribute('data-concord-href')) {
        el.setAttribute('href', el.getAttribute('data-concord-href'));
      }

      if (el.tagName === 'SCRIPT') {
        if (el.hasAttribute('data-concord-type')) {
          el.setAttribute('type', el.getAttribute('data-concord-type'));
        }
      }
      if (el.tagName === 'IFRAME' || el.tagName === 'IMG') {
        if (el.hasAttribute('data-concord-display')) {
          el.style.setProperty(
            'display',
            el.getAttribute('data-concord-display'),
          );
        } else {
          /* If the element does not have a data-concord-display attribute,
          clear the style.display attribute. */
          el.style.removeProperty('display');
        }
      }
      /* If the node is being changed from allowed to blocked, handle the node updates. */
    } else {
      el.removeAttribute('src');
      el.removeAttribute('href');
      if (el.tagName === 'SCRIPT') {
        if (el.hasAttribute('type')) {
          el.setAttribute('data-concord-type', el.type);
          el.setAttribute('type', 'text/plain');
        }
      }
      if (el.tagName === 'IFRAME' || el.tagName === 'IMG') {
        if (el.style.display) {
          el.setAttribute('data-concord-display', el.style.display);
        }
        el.style.setProperty('display', 'none');
      }
    }
  });

  coverBlockedIframesAndImages();
}

/**
 * Checks and potentially updates matching node clones that were previously
 * blocked by removing them from the DOM. Node clones are checked against the
 * regex pattern of a known tracker that is allowed for the user. If the node
 * clone matches the regex pattern, it will be added back to the DOM.
 *
 * @param {Object} tracker
 */
function updateRemovedNodes({ regex }) {
  const restoredNodes = [];
  store.removedNodes.forEach(({ node, parent }) => {
    const inlineValue = node.getAttribute('data-concord-inline');
    if (inlineValue?.match(regex)) {
      node.setAttribute('data-concord-modified', 'allowed');
      parent.appendChild(node);
    } else {
      restoredNodes.push({ node, parent }); // Keep this node in the array
    }
  });

  store.updateRemovedNodes(restoredNodes);
}

/**
 * Updates the consent state of all known trackers that are allowed for the
 * user. If the tracker is a script, iframe, or image, the currently blocked
 * element will be updated and changed to allowed. If the tracker is a cookie
 * and is allowed, no action will be taken. If the tracker is a cookie and is
 * not allowed, the cookie will be blocked/deleted.
 */
export function updateTrackerConsentState() {
  /* Get the blocking mode from the store and return 
  without doing anything if it is set to discovery or disabled. */
  const {
    config: {
      regionTemplate: {
        settings: { consent: { blockingMode: mode = 'disabled' } = {} } = {},
      } = {},
    } = {},
  } = store;
  if (mode === 'discovery' || mode === 'disabled') {
    return;
  }
  const trackers = store.getKnownTrackers();
  const managedElements = getElements();

  /* Filter out and process script, iframes, images, and links that have been modified. 
  Trackers are sorted so scripts are processed before iframe and image nodes. 
  All blocked cookies are processed after. */
  trackers.forEach((tracker) => {
    if (tracker.modifiedChanged) {
      if (
        tracker.type === 'script' ||
        tracker.type === 'iframe' ||
        tracker.type === 'image' ||
        tracker.type === 'link'
      ) {
        /* For inline nodes that have been removed, update the consent state. 
        We only need to handle blocked to allowed changes here. */
        if (store.removedNodes.length > 0 && tracker.modified === 'allowed') {
          updateRemovedNodes(tracker);
        }
        if (managedElements.length > 0) {
          updateNodeConsents(managedElements, tracker);
        }
      }
    }
    if (tracker.type === 'cookie' && tracker.modified === 'blocked') {
      blockCookie(tracker);
    }
  });
}
